import React, { useState, useEffect } from 'react';
import { Tooltip } from '@mui/material';
import { Add as AddIcon, Inventory2Outlined as PackagesIcon } from '@mui/icons-material';
import { DataProviderInstance } from '../../api/DataProvider';
import CustomButton from '../../components/buttons/CustomButton';
import AreaTitle from '../../components/areaTitle/AreaTitle';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { USER_ROLES } from '../../constants/userRoles';
import usePlanLock from '../../hooks/usePlanLock';
import { SUBSCRIPTION_PERMISSIONS } from '../../consts/SubscriptionFeatures';
import usePackageForm from './usePackageForm';
import PackageList from './components/PackageList';
import PackagesEmptyState from './components/PackagesEmptyState';
import PackagesLoading from './components/PackagesLoading';
import PackageFormPopup from './components/PackageFormPopup';
import Toast from '../../components/support/Toast';
import styles from './Packages.module.css';

const MANAGE_DENIED = 'Apenas proprietários e administradores podem criar e editar pacotes';

/**
 * Pacotes de serviços: a página só orquestra (carregar, abrir o formulário, salvar).
 * A lista, o estado vazio e o formulário vivem em ./components; as contas em
 * packageFormat.js e o estado do formulário em usePackageForm.js.
 */
const Packages = () => {
  const { hasRole } = useRoleAccess();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [toast, setToast] = useState({ open: false, message: '' });

  // Só proprietário e administradores criam e editam
  const canManagePackages = hasRole([USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN]);
  // Cadeado por plano: a lista continua visível; criar/editar abre o modal de upgrade.
  const lock = usePlanLock(SUBSCRIPTION_PERMISSIONS.SERVICE_PACKAGES, 'Pacotes de serviços', undefined, { plural: true });

  const dataProvider = DataProviderInstance();
  const procedures = dataProvider.CompanyConfig.Procedures || [];
  const form = usePackageForm(procedures);

  const loadPackages = () => {
    dataProvider.GetPackages((response) => {
      if (response.success) setPackages(response.data || []);
      setLoading(false);
    });
  };

  useEffect(() => {
    loadPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenDialog = (pkg = null) => {
    form.reset(pkg);
    setSaving(false);
    setSaveError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    form.reset(null);
  };

  const handleSave = () => {
    const packageData = form.buildPayload();
    if (!packageData) return;

    const successMessage = form.editingPackage ? 'Pacote atualizado' : 'Pacote criado';
    const done = (response) => {
      setSaving(false);
      if (response?.success) {
        loadPackages();
        handleCloseDialog();
        setToast({ open: true, message: successMessage });
        return;
      }
      // Erro do servidor fica no popup, sem perder o que foi preenchido
      setSaveError(response?.message || response?.error || 'Não foi possível salvar o pacote. Tente novamente.');
    };

    setSaveError('');
    setSaving(true);
    if (form.editingPackage) {
      dataProvider.UpdatePackage(form.editingPackage.package_id, packageData, done);
    } else {
      dataProvider.CreatePackage(packageData, done);
    }
  };

  const openNew = lock.guard(() => handleOpenDialog());

  return (
    <div className={`page-content ${styles.page}`}>
      <div className={styles.container}>
        {lock.banner}
        {lock.modal}

        <div className={styles.header}>
          <AreaTitle
            Title="Pacotes de Serviços"
            Description="Sessões vendidas juntas, com preço e validade próprios."
            Icon={<PackagesIcon />}
          />
          <Tooltip title={canManagePackages ? '' : MANAGE_DENIED} arrow>
            <span>
              <CustomButton
                style="main"
                variant="contained"
                label="Novo pacote"
                icon={<AddIcon />}
                onClick={openNew}
                disabled={!canManagePackages}
              />
            </span>
          </Tooltip>
        </div>

        {loading ? (
          <PackagesLoading />
        ) : packages.length > 0 ? (
          <PackageList
            packages={packages}
            procedures={procedures}
            canManage={canManagePackages}
            deniedReason={MANAGE_DENIED}
            onEdit={(pkg) => lock.guard(() => handleOpenDialog(pkg))()}
          />
        ) : (
          <PackagesEmptyState
            canManage={canManagePackages}
            deniedReason={MANAGE_DENIED}
            onCreate={openNew}
          />
        )}

        {openDialog && (
          <PackageFormPopup
            form={form}
            procedures={procedures}
            saving={saving}
            error={saveError}
            onClose={handleCloseDialog}
            onSave={handleSave}
          />
        )}

        <Toast
          open={toast.open}
          onClose={() => setToast({ ...toast, open: false })}
          message={toast.message}
          severity="success"
          autoHideDuration={6000}
        />
      </div>
    </div>
  );
};

export default Packages;
